import React, { useState, useRef, useEffect } from "react";
import Lottie from "lottie-react";
import GirlLottie from "../assets/json/sezione-03-img-01.json";
import GirlSvg from "../assets/images/sezione-03-img-01.svg";
import OutlineSvg from "../assets/images/sezione-03-img-02.svg";
import arrowIcon from "../assets/images/Vector_btn.png";
import BackSection from "../assets/images/bkg_giallo.svg";
import Sezione3popup1 from "./popup/it/sezione-3-popup-1";

export default function Sezione3() {
  
  const [open1, setOpen1] = useState(false);

  const girlRef = useRef(null);
  const bgRef   = useRef(null);

  const girlIn = useInView(girlRef, "0px");
  const bgIn   = useInView(bgRef, "0px");

  const lottieRef = useRef(null);
  const hasPlayedRef = useRef(false);

  useEffect(() => {
    if (girlIn && !hasPlayedRef.current) {
      // Avvia dall'inizio quando entra in viewport
      lottieRef.current?.goToAndPlay(0, true);
      hasPlayedRef.current = true;
    }
  }, [girlIn]);

  // Tooltip "Settori" (solo mobile): chiude su click fuori o ESC
  const [sectorsOpen, setSectorsOpen] = useState(false);
  const sectorsRef = useRef(null);

  useEffect(() => {
    if (!sectorsOpen) return;
    const onDocDown = (e) => {
      if (sectorsRef.current && !sectorsRef.current.contains(e.target)) setSectorsOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setSectorsOpen(false);
    document.addEventListener("mousedown", onDocDown);
    document.addEventListener("touchstart", onDocDown);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      document.removeEventListener("touchstart", onDocDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [sectorsOpen]);

  // --- utils fuori dal componente (o in cima dentro al file) ---
function useInView(ref, rootMargin = "0px") {
  const [inView, set] = React.useState(false);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && set(true),
      { root: null, rootMargin, threshold: 0.2 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref, rootMargin]);
  return inView;
}

function formatNumber(val, decimals = 0, unit = null) {
  if (val == null || isNaN(val)) return "—";
  const fmt = Number(val).toLocaleString("it-IT", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  if (unit === "pct") return `${fmt}%`;
  if (unit === "eur") return fmt; // aggiungi € se ti serve
  return fmt;
}

function Counter({
  value,
  duration = 1200,
  decimals = 0,
  unit = null,
  className = "",
  note,
}) {
  const ref = React.useRef(null);
  const inView = useInView(ref, "0px");
  const [display, setDisplay] = React.useState("—");

  React.useEffect(() => {
    if (!inView || value == null) return;
    let raf, start;
    const from = 0;
    const to = Number(value);
    const easeOut = (t) => 1 - Math.pow(1 - t, 3);

    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min(1, (ts - start) / duration);
      const eased = easeOut(p);
      const curr = from + (to - from) * eased;
      setDisplay(formatNumber(curr, decimals, unit));
      if (p < 1) raf = requestAnimationFrame(step);
    };

    // valore iniziale
    setDisplay(formatNumber(0, decimals, unit));
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, duration, decimals, unit]);

  return (
    <div>
      <div ref={ref} className={className}>{display}</div>      
    </div>
  );
}

const Stat = ({ value, label, info, labelClassName = "" }) => (
  <div className="flex flex-col items-start md:items-center">
    <Counter
      value={value}
      decimals={0}        // metti 1-2 se vuoi decimali
      className="text-[16px] md:text-[28px] lg:text-[32px] font-bold text-[#0E3761] leading-none mb-2"
    />
    <div className={`text-[#0E3761] font-bold text-[14px] md:text-[16px] lg:text-[20px] text-left md:text-center ${labelClassName}`}>
      {label}
      {info}
    </div>
  </div>
);

  return (
    <>
    <section id="sezione-perimetro" className="w-full bg-[#F7B500] py-10 md:pt-24 md:pb-0" 
        style={{
            backgroundImage: `url(${BackSection})`,
            backgroundSize: 'cover',
        }}>

      <div className="mx-auto w-full xl:w-[1440px] px-4 md:px-5 xl:px-0 flex flex-col md:flex-row gap-0 md:gap-0">

        {/* Colonna sinistra */}
        <div ref={girlRef} className="w-1/3 hidden md:flex justify-center md:justify-end">
          <Lottie 
            animationData={GirlLottie}
            loop
            autoplay={false}
            lottieRef={lottieRef}
            style={{ height: 584 }}
            className="mb-0 md:mb-[-10%] xl:mb-[-15px]"
          />
        </div>

        {/* Colonna destra */}
        <div className="w-full md:w-2/3 flex">
          {/* Box con bordo SVG come background */}
          <div
            ref={bgRef}
            className={[
              "flex flex-col justify-start items-center md:items-start",
              "p-10 md:px-[80px]",
              "bg-none",
              "md:[background-image:var(--bg)]",
              "md:bg-no-repeat md:bg-top md:bg-contain",
              "border-2 border-[#002655] rounded-[50px] md:border-0",
              "gap-5 relative",
              "transition-all duration-[4000ms] ease-[cubic-bezier(0.25,0.46,0.45,0.94)] will-change-transform",
              // Effetto fade-in per il background - solo su md e superiori
              bgIn 
                ? "md:opacity-100 md:scale-100" 
                : "md:opacity-0 md:scale-95"
            ].join(" ")}
            style={{ 
              "--bg": `url(${OutlineSvg})`,
            }}
          >
            <h2 className="text-[#0E3761] mb-5 md:mb-1 leading-none">
              Perimetro di consolidamento
            </h2>

            <img src={GirlSvg} alt="" className="md:hidden max-w-[160px] w-full absolute top-[35%] left-2 select-none pointer-events-none" />

            {/* Stats + lista comparti */}
            <div className="grid grid-cols-2 md:grid-cols-6 items-start pl-[120px] md:pl-[0px] gap-6 md:gap-0">
              <Stat value={4} label="Paesi" />
              <Stat
                value={1}
                label={
                  <>
                    Capogruppo
                    Società Benefit
                  </>
                }
              />
              <Stat value={23} label="Società" />

              {/* Settori: da mobile la lista dei comparti sta in un tooltip (icona i) */}
              <div ref={sectorsRef} className="relative">
                <Stat
                  value={5}
                  label="Settori"
                  labelClassName="flex items-center gap-1.5 md:block"
                  info={
                    <button
                      type="button"
                      onClick={() => setSectorsOpen((v) => !v)}
                      aria-expanded={sectorsOpen}
                      aria-label="Mostra l'elenco dei settori"
                      className="md:hidden inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border-[1.5px] border-[#0E3761] text-[11px] leading-none"
                    >
                      i
                    </button>
                  }
                />

                {sectorsOpen && (
                  <div
                    role="tooltip"
                    className="md:hidden absolute z-30 right-0 top-full mt-2 w-[190px] max-w-[70vw] rounded-xl border border-[#0E3761]/25 bg-white shadow-xl p-3 text-[13px] leading-snug font-bold text-[#0E3761] text-left"
                  >
                    Assicurativo<br/>
                    Servizi<br/>
                    Immobiliare<br/>
                    Holding Assicurative<br/>
                    Holding di Servizi
                  </div>
                )}
              </div>

              {/* Lista comparti: solo da desktop (su mobile e' nel tooltip) */}
              <p className="hidden md:block col-span-2 md:col-span-2 text-[#0E3761] space-y-1 md:pl-6 md:border-l text-[14px] md:text-[20px] text-right md:text-left pr-5 md:pr-0 border-[#0E3761]/40 md:border-[#0E3761]/40">
                Assicurativo<br/>
                Servizi<br/>
                Immobiliare<br/>
                Holding Assicurative<br/>
                Holding di Servizi<br/>
              </p>
            </div>

            {/* Bottone */}
            <button 
              className="bt-secondary mt-4 md:mt-[-55px] lg:mt-[-15px] flex items-center gap-2"
              onClick={() => setOpen1(true)}
              >
              <img src={arrowIcon} alt="" className="w-[24px] h-[24px]" />
              Scopri di più
            </button>
          </div>
        </div>
      </div>
    </section>

    <Sezione3popup1 isOpen={open1} onClose={() => setOpen1(false)} />
    
    </>
  );
}